import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
// mui
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Unstable_Grid2';
import MenuItem from '@mui/material/MenuItem';
// validation
import { yupResolver } from '@hookform/resolvers/yup';
import { AdminsManagementUpdateSchema } from '../../../../utils/validation';
// actions
import { getAllMain } from '../../../../redux/actions/admin/mainAction';
import { closeModal } from '../../../../redux/slices/modal';
import { openAlert } from '../../../../redux/slices/alert';
import { updateAdmin } from '../../../../redux/actions/admin/adminAction';

const AdminsManagementEdit = () => {
  // state
  const { item } = useSelector((state) => state.modal);
  const { mains } = useSelector((state) => state.mains);
  // console.log('🚀 ~ file: AdminsManagementEdit.js ~ line 26 ~ AdminsManagementEdit ~ mains', mains);
  const { loading } = useSelector((state) => state.admins);

  const {
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    control,
  } = useForm({
    resolver: yupResolver(AdminsManagementUpdateSchema),
    defaultValues: {
      name: '',
      username: '',
      email: '',
      status: '',
      main_id: '',
      password: '',
      password_confirmation: '',
    },
  });

  // dispatch
  const dispatch = useDispatch();

  // i18next
  const { t } = useTranslation();

  // functions
  const onSubmit = async (data) => {
    await dispatch(updateAdmin({ id: item.id, data }))
      .unwrap()
      .then(async () => {
        dispatch(openAlert());
        dispatch(closeModal());
        reset();
      })
      .catch((errors) => {
        if (errors.errors) {
          Object.keys(errors.errors).forEach((error) => {
            setError(error, {
              type: error,
              message: errors.errors[error][0],
            });
          });
        } else {
          dispatch(closeModal());
          dispatch(openAlert('error'));
        }
      });
  };

  // useEffect
  useEffect(() => {
    reset({
      name: item.name,
      username: item.username,
      email: item.email,
      status: item.status,
      main_id: item.main_administration?.id,
    });

    (async () => {
      await dispatch(getAllMain());
    })();
  }, [dispatch, item, reset]);

  return (
    <>
      <DialogContent>
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Grid xs={12} md={6}>
            <Controller
              name={'name'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  type={'text'}
                  onChange={onChange}
                  value={value}
                  error={!!errors.name}
                  label={t('Name')}
                  helperText={errors.name?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <Controller
              name={'username'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  type={'text'}
                  onChange={onChange}
                  value={value}
                  error={!!errors.username}
                  label={t('User Name')}
                  helperText={errors.username?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12} md={12}>
            <Controller
              name={'email'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  type={'text'}
                  onChange={onChange}
                  value={value}
                  error={!!errors.email}
                  label={t('Email')}
                  helperText={errors.email?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12} md={12}>
            <Controller
              name={'main_id'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  select
                  label={t('Name Main Administrations')}
                  value={value}
                  onChange={onChange}
                  error={!!errors.main_id}
                  helperText={errors.main_id?.message}
                >
                  <MenuItem value="">
                    <em>{t('None')}</em>
                  </MenuItem>
                  {mains && mains.length > 0
                    ? mains.map((main) => (
                        <MenuItem key={main.id} value={main.id}>
                          {main.name}
                        </MenuItem>
                      ))
                    : null}
                </TextField>
              )}
            />
          </Grid>
          <Grid xs={12} md={12}>
            <Controller
              name={'status'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  select
                  label={t('Action Status')}
                  value={value}
                  onChange={onChange}
                  error={!!errors.status}
                  helperText={errors.status?.message}
                >
                  <MenuItem value="active">{t('active')}</MenuItem>
                  <MenuItem value="inactive">{t('inactive')}</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dispatch(closeModal())}>{t('Close')}</Button>
        <LoadingButton
          color="success"
          loading={loading}
          loadingIndicator={t('Loading…')}
          onClick={handleSubmit(onSubmit)}
        >
          {t('Save')}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default AdminsManagementEdit;
